
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {AccountUpdateResponseParams} from './AccountUpdateResponseParams';
import {FieldModelAccountUpdateResponseParams} from './AccountUpdateResponseParams';

/**
 * AccountUpdateResponse struct
 */
class AccountUpdateResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!AccountUpdateResponseParams=} accountParams
   * @constructor
   */
  constructor (parent = new Response(), accountParams = new AccountUpdateResponseParams()) {
    super()
    super.copy(parent)
    this.AccountParams = accountParams
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountUpdateResponse}
   * @param {!AccountUpdateResponse} other Other struct
   * @returns {!AccountUpdateResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.AccountParams != null) {
      this.AccountParams = AccountUpdateResponseParams.fromObject(other.AccountParams)
    } else {
      this.AccountParams = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountUpdateResponse}
   * @returns {!AccountUpdateResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountUpdateResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountUpdateResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountUpdateResponse}
   * @param {!AccountUpdateResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountUpdateResponse)) {
      throw new TypeError('Instance of AccountUpdateResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountUpdateResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      AccountParams: ((this.AccountParams != null) ? this.AccountParams : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountUpdateResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountUpdateResponse} other Object value
   * @returns {!AccountUpdateResponse} Created struct
   */
  static fromObject (other) {
    return new AccountUpdateResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountUpdateResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountUpdateResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountUpdateResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4007
  }
}

export { AccountUpdateResponse };

/**
 * Fast Binary Encoding AccountUpdateResponse field model
 */
class FieldModelAccountUpdateResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.AccountParams = new FieldModelAccountUpdateResponseParams(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.AccountParams.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.AccountParams.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountUpdateResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4007
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountParams.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountParams.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountParams.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!AccountUpdateResponse} fbeValue Default value, defaults is new AccountUpdateResponse()
   * @returns {!AccountUpdateResponse} AccountUpdateResponse value
   */
  get (fbeValue = new AccountUpdateResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!AccountUpdateResponse} fbeValue AccountUpdateResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountParams.fbeSize) <= fbeStructSize) {
      fbeValue.AccountParams = this.AccountParams.get()
    } else {
      fbeValue.AccountParams = new AccountUpdateResponseParams()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountParams.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountUpdateResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountUpdateResponse}
   * @param {!AccountUpdateResponse} fbeValue AccountUpdateResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountUpdateResponse}
   * @param {AccountUpdateResponse} fbeValue AccountUpdateResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.AccountParams.set(fbeValue.AccountParams)
  }
}

export { FieldModelAccountUpdateResponse };

/**
 * Fast Binary Encoding AccountUpdateResponse model
 */
class AccountUpdateResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountUpdateResponse(this.buffer, 4)
  }

  /**
   * Get the AccountUpdateResponse model
   * @this {!AccountUpdateResponseModel}
   * @returns {!FieldModelAccountUpdateResponse} model AccountUpdateResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountUpdateResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountUpdateResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountUpdateResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountUpdateResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountUpdateResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountUpdateResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountUpdateResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountUpdateResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountUpdateResponseModel}
   * @param {!AccountUpdateResponse} value AccountUpdateResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountUpdateResponseModel}
   * @param {!AccountUpdateResponse} value AccountUpdateResponse value, defaults is new AccountUpdateResponse()
   * @return {!object} Deserialized AccountUpdateResponse value and its size
   */
  deserialize (value = new AccountUpdateResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountUpdateResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountUpdateResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountUpdateResponseModel}
   * @param {!number} prev Previous AccountUpdateResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountUpdateResponseModel };
